import React from "react";
import { AlertCircleIcon, Loader2Icon } from "lucide-react";
import { AuthForm } from "../auth/AuthForm";
import { useAuth } from "../../contexts/AuthContext";
import type { Service } from "../../types/booking";
interface DetailsStepProps {
  service: Service;
  hasPrescription: boolean;
  onPrescriptionChange: (value: boolean) => void;
  note: string;
  onNoteChange: (value: string) => void;
  canConfirm: boolean;
  submitting: boolean;
  error: string | null;
  onConfirm: () => void;
  onChangeSlot: () => void;
}
export function DetailsStep({
  service,
  hasPrescription,
  onPrescriptionChange,
  note,
  onNoteChange,
  canConfirm,
  submitting,
  error,
  onConfirm,
  onChangeSlot
}: DetailsStepProps) {
  const {
    user,
    logout
  } = useAuth();
  if (!user) {
    return <div data-id="element-92" data-mp="al3dpuxwgo8r">
        <h2 className="text-xl font-semibold text-ink" data-id="element-93" data-mp="aktklyhk60yu">Identifiez-vous pour confirmer</h2>
        <p className="mt-1 text-sm text-muted" data-id="element-94" data-mp="5tbjnafjc0dv">
          Votre créneau est conservé. Votre compte vous permet de retrouver et gérer vos rendez-vous.
        </p>
        <div className="mt-6 max-w-md" data-id="element-95" data-mp="2u5wy8ya60f1u">
          <AuthForm data-id="element-96" data-mp="2z36mwbpqcmom" />
        </div>
      </div>;
  }
  return <div data-id="element-97" data-mp="2z4rkybb96nr8">
      <h2 className="text-xl font-semibold text-ink" data-id="element-98" data-mp="2lbs3i3rwujh1">Vérifiez et confirmez</h2>

      <div className="mt-6 rounded-2xl bg-surface p-5" data-id="element-99" data-mp="2l9pffpabj0ey">
        <div className="flex items-start justify-between gap-4" data-id="element-100" data-mp="2pc2b1kvcphy0">
          <div data-id="element-101" data-mp="2bdm1zna0nfcp">
            <p className="text-xs text-muted" data-id="element-102" data-mp="2bd2ul0hzs9cx">Patient</p>
            <p className="mt-1 font-medium text-ink" data-id="element-103" data-mp="2bfp3p4kj6wx9">
              {user.firstName} {user.lastName}
            </p>
            <p className="mt-0.5 text-sm text-muted" data-id="element-104" data-mp="22cxnyskmhezt">
              {user.email} · {user.phone}
            </p>
          </div>
          <button type="button" onClick={logout} className="whitespace-nowrap text-sm font-medium text-brand hover:underline" data-id="element-105" data-mp="26dhng2so4up4">
            Pas vous ?
          </button>
        </div>
      </div>

      {service.requiresPrescription && <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-line p-5" data-id="element-106" data-mp="4m3d8480sp94">
          <input type="checkbox" checked={hasPrescription} onChange={e => onPrescriptionChange(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line text-brand focus:ring-brand" data-id="element-107" data-mp="1dhqj0ywxrcwl" />
          <span data-id="element-108" data-mp="zktvb6c46bjx">
            <span className="block text-sm font-medium text-ink" data-id="element-109" data-mp="zlda4e6gwjtq">J'ai une ordonnance de mon médecin</span>
            <span className="mt-0.5 block text-sm text-muted" data-id="element-110" data-mp="1w9t36k8sdbbl">
              Ce soin est réalisé sur prescription. Pensez à l'apporter avec votre carte Vitale.
            </span>
          </span>
        </label>}

      <div className="mt-6" data-id="element-111" data-mp="1nfwht7ng42x8">
        <label htmlFor="note" className="block text-sm font-medium text-ink" data-id="element-112" data-mp="1ij62imjgtbcv">
          Motif de consultation <span className="font-normal text-muted" data-id="element-113" data-mp="2fy1uyt6f3fak">(facultatif)</span>
        </label>
        <textarea id="note" rows={3} value={note} onChange={e => onNoteChange(e.target.value)} placeholder="Ex. douleur à l'épaule droite depuis 3 semaines" className="mt-1.5 block w-full resize-none rounded-xl border border-line px-4 py-3 text-sm text-ink placeholder:text-muted/60 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" data-id="element-114" data-mp="2b7lgd5qv1nmm" />
      </div>

      {error && <div role="alert" className="mt-6 flex items-start gap-2 rounded-xl bg-danger/5 px-4 py-3 text-sm text-danger" data-id="element-115" data-mp="2wk2i4sv9uluf">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-116" data-mp="31fhufgdms9oq" />
          <span data-id="element-117" data-mp="36f479opjrnx0">
            {error}{" "}
            <button type="button" onClick={onChangeSlot} className="font-medium underline" data-id="element-118" data-mp="3b7njoici0d08">
              Choisir un autre créneau
            </button>
          </span>
        </div>}

      <div className="mt-8 flex flex-col-reverse gap-4 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-119" data-mp="3k83llndlfc2z">
        <p className="text-xs text-muted" data-id="element-120" data-mp="3p6ew80bppqm9">Annulation gratuite jusqu'à 24 h avant le rendez-vous.</p>
        <button type="button" onClick={onConfirm} disabled={!canConfirm || submitting} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-line disabled:text-muted" data-id="element-121" data-mp="3bjo5k84ozywg">
          {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-122" data-mp="2sl25mwkwwdu4" />}
          {submitting ? "Réservation…" : "Confirmer le rendez-vous"}
        </button>
      </div>
    </div>;
}